import { useCallback, useEffect, useRef, useState } from 'react';
import { podeFazer } from '../acesso';
import type { Eu } from '../acesso';
import { sessaoAtual } from '../auth';
import { EstadoVazio } from '../design/Estados';
import { Icone } from '../design/Icone';
import { dataHoraBr, lerNotificacoes, marcarLidas } from '../encomendas-fluxo';
import type { Notificacao } from '../encomendas-fluxo';
import './sino.css';

/**
 * O SINO de verdade (fase 10): as notificações das Encomendas (comprada, NF identificada, chegou,
 * pronta, divergência, prazo, parada). Sem notificação: o estado vazio do design e sem ponto —
 * nunca um número inventado. Relê a cada minuto; abrir marca como lidas.
 */

const DESTINO = (n: Notificacao, eu: Eu | null) =>
  n.tipo === 'nota_bonificacao'
    ? '#/encomendas-recebimento'
    : n.tipo === 'divergencia' || n.tipo === 'encomenda_parada'
      ? '#/encomendas-todas'
      : podeFazer(eu, 'encomendas.ver_proprias')
        ? '#/encomendas-minhas'
        : '#/encomendas-recebimento';

export function Sino({
  eu,
  classe,
  painelClasse,
}: {
  eu: Eu | null;
  classe: string;
  painelClasse: string;
}) {
  const temModulo =
    podeFazer(eu, 'encomendas.ver_proprias') ||
    podeFazer(eu, 'encomendas.receber') ||
    podeFazer(eu, 'encomendas.ver_todas');
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState<{
    naoLidas: number;
    notificacoes: readonly Notificacao[];
  } | null>(null);
  const caixa = useRef<HTMLDivElement>(null);

  const ler = useCallback(() => {
    if (!temModulo) return;
    void lerNotificacoes().then((r) => r.ok && setDados(r.dados));
  }, [temModulo]);
  useEffect(() => {
    ler();
    const t = window.setInterval(ler, 60_000);
    return () => window.clearInterval(t);
  }, [ler]);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', fora);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      window.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const abrir = async () => {
    const vai = !aberto;
    setAberto(vai);
    if (vai && dados && dados.naoLidas > 0) {
      const s = await sessaoAtual();
      if (s) {
        await marcarLidas(s, null);
        setDados({ ...dados, naoLidas: 0 });
      }
    }
  };

  const n = dados?.naoLidas ?? 0;
  return (
    <div className="sino" ref={caixa}>
      <button
        type="button"
        className={classe}
        aria-label={n > 0 ? `Notificações (${String(n)} novas)` : 'Notificações (nenhuma nova)'}
        aria-expanded={aberto}
        onClick={() => void abrir()}
      >
        <Icone nome="bell" tamanho={17} traco={1.5} />
        {n > 0 && <span className="sino-ponto">{n > 9 ? '9+' : String(n)}</span>}
      </button>
      {aberto && (
        <div className={`${painelClasse} sino-painel`} role="dialog" aria-label="Notificações">
          <div className="sh-painel-topo">Notificações</div>
          {!dados || dados.notificacoes.length === 0 ? (
            <EstadoVazio
              icone="bell"
              titulo="Nenhuma notificação"
              texto="Você está em dia. Avisamos aqui quando algo precisar de você."
            />
          ) : (
            <ul className="sino-lista">
              {dados.notificacoes.map((x) => (
                <li key={x.id} className={x.lida ? '' : 'sino-nova'}>
                  <a href={DESTINO(x, eu)} onClick={() => setAberto(false)}>
                    <b>{x.titulo}</b>
                    <span>{x.texto}</span>
                    <time>{dataHoraBr(x.criadaEm)}</time>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
